/**
 * Route-model reconciliation (Phase 2b).
 *
 * The BFF emits a `{type:"route",route}` SSE event whose `route` payload is the
 * browser-facing {@link RoutePayload} (`crates/mcp-web/src/route.rs#RoutePayload`):
 * camelCased, carrying real per-floor GeoJSON line features, the levels crossed,
 * distance/ETA, the routing engine's raw steps, an accessible flag and a
 * best-effort destination label.
 *
 * This module maps that wire payload onto the 1a UI {@link Route} view-model the
 * chat/map components already render:
 *  - {@link RouteGeometry} (legs per level + origin/dest/floor-change points) so
 *    `route-overlay.ts#buildRouteOverlay` can draw the teal polyline + markers;
 *  - formatted `dist`/`eta` strings + `floors`/`startFloor`/`destFloor` for the
 *    RouteCard header, the FloorSwitcher highlight and MapFallback;
 *  - turn-by-turn {@link RouteStep}s synthesised from the engine steps.
 *
 * Everything is best-effort + defensive: the routing engine's step shape is not
 * pinned, so unknown fields degrade to readable defaults rather than throwing.
 */
import { z } from "zod";

import type { LngLat, Route, RouteGeometry, RouteStep, Transition } from "@/lib/types";

/* ── Wire schema for the SSE `route` payload ───────────────────────────── */

/** Permissive schema for the BFF `route` event payload (camelCase). */
export const routePayloadSchema = z
  .object({
    perFloorGeoJSON: z.array(z.unknown()).default([]),
    levels: z.array(z.number()).default([]),
    distanceMeters: z.number().nullable().optional(),
    etaSeconds: z.number().nullable().optional(),
    steps: z.array(z.record(z.unknown())).default([]),
    accessible: z.boolean().default(false),
    destName: z.string().nullable().optional(),
  })
  .passthrough();

export type RoutePayload = z.infer<typeof routePayloadSchema>;

/** Parse an unknown SSE `route` value into a {@link RoutePayload}, or `null`. */
export function parseRoutePayload(value: unknown): RoutePayload | null {
  if (value == null) return null;
  const parsed = routePayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/* ── GeoJSON line-feature helpers (no @types/geojson dependency) ────────── */

interface LineFeatureLike {
  geometry?: { type?: string; coordinates?: unknown } | null;
  properties?: Record<string, unknown> | null;
}

const isLngLat = (v: unknown): v is LngLat =>
  Array.isArray(v) &&
  v.length >= 2 &&
  typeof v[0] === "number" &&
  typeof v[1] === "number";

function featureLevel(f: LineFeatureLike): number {
  const raw = f.properties?.["level"] ?? f.properties?.["ordinal"];
  return Math.round(Number(raw ?? 0)) || 0;
}

/**
 * Best-effort transition mode for a level change, read from whichever of the
 * (unpinned) routing-engine property names the leg feature happens to carry —
 * `class` / `usecase` / `type` / `mode` / `levelchanger`. Returns `undefined`
 * when nothing recognisable is present, so the marker falls back to a generic
 * "floor change" glyph rather than mislabelling. Verify prop names on live data.
 */
function levelChangerKind(
  props: Record<string, unknown> | null | undefined,
): Transition | undefined {
  if (!props) return undefined;
  const raw =
    str(props["levelchanger"]) ??
    str(props["class"]) ??
    str(props["usecase"]) ??
    str(props["type"]) ??
    str(props["mode"]) ??
    str(props["transition"]);
  if (!raw) return undefined;
  const v = raw.toLowerCase();
  if (v.includes("elevator") || v.includes("lift")) return "elevator";
  if (v.includes("escalator")) return "escalator";
  if (v.includes("stair") || v.includes("steps")) return "stairs";
  return undefined;
}

function featureCoords(f: LineFeatureLike): LngLat[] {
  const g = f.geometry;
  if (!g || g.type !== "LineString" || !Array.isArray(g.coordinates)) return [];
  return (g.coordinates as unknown[])
    .filter(isLngLat)
    .map((c) => [c[0], c[1]] as LngLat);
}

/**
 * Build {@link RouteGeometry} (legs per level + origin/dest/floor-change points)
 * and the ORDERED list of levels the path actually traverses (path order, not
 * sorted) from the per-floor line features.
 */
function buildGeometry(features: LineFeatureLike[]): {
  geometry: RouteGeometry;
  orderedLevels: number[];
} {
  const legs: Record<number, LngLat[]> = {};
  const orderedLevels: number[] = [];
  const floorChanges: NonNullable<RouteGeometry["floorChanges"]> = [];

  let origin: LngLat | undefined;
  let dest: LngLat | undefined;
  let prevLevel: number | undefined;
  let prevEnd: LngLat | undefined;

  for (const f of features) {
    const coords = featureCoords(f);
    if (coords.length === 0) continue;
    const level = featureLevel(f);

    legs[level] = (legs[level] ?? []).concat(coords);
    if (orderedLevels.at(-1) !== level) orderedLevels.push(level);

    origin ??= coords[0];
    dest = coords.at(-1);

    // Floor change between consecutive legs → marker at the previous leg's end.
    // `nextLevel` drives the "advance to next floor" affordance; `kind` (when the
    // leg feature carries a levelchanger class/usecase/type) picks a distinct
    // elevator / escalator / stairs icon — else left undefined (generic marker).
    if (prevLevel !== undefined && prevEnd && level !== prevLevel) {
      floorChanges.push({
        coord: prevEnd,
        level: prevLevel,
        dir: level > prevLevel ? "↑" : "↓",
        nextLevel: level,
        kind: levelChangerKind(f.properties),
      });
    }
    prevLevel = level;
    prevEnd = coords.at(-1);
  }

  return {
    geometry: { legs, origin, dest, floorChanges },
    orderedLevels,
  };
}

/* ── Steps ─────────────────────────────────────────────────────────────── */

const num = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined;

/**
 * Level-changer kinds from the engine's step directions
 * (`UP_/DOWN_{ELEVATOR,ESCALATOR,STAIRS}`), one per floor change, in route
 * order. Authoritative — the engine sets them from the level changer it used.
 */
export function stepTransitions(raw: Record<string, unknown>[]): Transition[] {
  const out: Transition[] = [];
  for (const s of raw) {
    const m = str(s["direction"])?.toUpperCase().match(/^(UP|DOWN)_(ELEVATOR|ESCALATOR|STAIRS)$/);
    if (!m) continue;
    out.push(m[2] === "ELEVATOR" ? "elevator" : m[2] === "ESCALATOR" ? "escalator" : "stairs");
  }
  return out;
}

/** Humanise a routing-engine direction enum into an instruction. */
function humanizeDirection(dir: string): string {
  switch (dir.toUpperCase()) {
    case "FORWARD":
    case "STRAIGHT":
    case "CONTINUE":
      return "Continue straight ahead";
    case "LEFT":
    case "TURN_LEFT":
      return "Turn left";
    case "RIGHT":
    case "TURN_RIGHT":
      return "Turn right";
    case "SLIGHT_LEFT":
      return "Bear slightly left";
    case "SLIGHT_RIGHT":
      return "Bear slightly right";
    case "UTURN":
    case "U_TURN":
      return "Make a U-turn";
    default:
      return "Continue";
  }
}

function stepText(s: Record<string, unknown>): string {
  return (
    str(s["instruction"]) ??
    str(s["text"]) ??
    str(s["name"]) ??
    str(s["html_instructions"]) ??
    (str(s["direction"]) ? humanizeDirection(s["direction"] as string) : undefined) ??
    "Continue ahead"
  );
}

/** Build the turn-by-turn {@link RouteStep} list from the engine steps. */
function buildSteps(
  raw: Record<string, unknown>[],
  levels: number[],
  destName: string | undefined,
): RouteStep[] {
  const steps: RouteStep[] = [];
  let n = 1;
  let prevLevel = levels[0] ?? 0;

  for (const s of raw) {
    const level = Math.round(num(s["level"]) ?? prevLevel) || prevLevel;
    if (level !== prevLevel) {
      steps.push({
        badge: "⇅",
        text: `Change to Level ${level}`,
        sub: `Floor change · L${prevLevel} → L${level}`,
        floor: true,
      });
      prevLevel = level;
    }
    const dist = num(s["distance"]) ?? num(s["distance_meters"]) ?? num(s["length"]);
    steps.push({
      badge: String(n++),
      text: stepText(s),
      sub:
        dist !== undefined
          ? `${Math.round(dist)} m · Level ${level}`
          : `Level ${level}`,
      distM: dist,
    });
  }

  // Fallback when the engine returned no steps — at least show "follow the route".
  if (steps.length === 0) {
    steps.push({
      badge: "1",
      text: "Follow the highlighted route on the map",
      sub: `Level ${levels[0] ?? 0}`,
    });
  }

  steps.push({
    badge: "✓",
    text: destName ? `${destName} is ahead` : "You have arrived",
    sub: "Arrive",
    done: true,
  });

  return steps;
}

/* ── Formatting ────────────────────────────────────────────────────────── */

function formatDistance(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return "—";
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function formatEta(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return "—";
  const min = Math.max(1, Math.round(s / 60));
  return `${min} min`;
}

/* ── Payload → Route view-model ────────────────────────────────────────── */

/**
 * Map a BFF {@link RoutePayload} onto the UI {@link Route} view-model, attaching
 * the drawable {@link RouteGeometry}. The `geometry` field marks the route as
 * "live" (vs. the static demo scenes).
 */
export function routeFromPayload(p: RoutePayload): Route {
  const features = (p.perFloorGeoJSON as LineFeatureLike[]) ?? [];
  const { geometry, orderedLevels } = buildGeometry(features);

  const levels =
    orderedLevels.length > 0
      ? orderedLevels
      : p.levels.map((n) => Math.round(n));

  const startFloor = levels[0] ?? 0;
  const destFloor = levels.at(-1) ?? startFloor;
  const uniqueFloors = Array.from(new Set(levels));

  const accessible = p.accessible;
  // The level changers the engine ACTUALLY used, in route order — never a
  // guess from the accessible flag (that told users to "take the elevator" on
  // routes that avoid elevators). Unknown → null: the legend and map chip
  // then say a neutral "floor change".
  const used = stepTransitions(p.steps);
  const transition: Transition | null = uniqueFloors.length > 1 ? (used[0] ?? null) : null;
  (geometry.floorChanges ?? []).forEach((fc, i) => {
    fc.kind ??= used[i];
  });

  const destName = str(p.destName ?? undefined) ?? "Destination";

  return {
    destName,
    floors: uniqueFloors,
    startFloor,
    destFloor,
    accessible,
    transition,
    dist: formatDistance(p.distanceMeters),
    eta: formatEta(p.etaSeconds),
    steps: buildSteps(p.steps, levels, str(p.destName ?? undefined)),
    geometry,
  };
}
